import { accessToken } from '@/auth/oidc'
import { BASE_URL } from '@/lib/api'
import { registerDevice, unregisterDevice, type RegisteredDevice } from './pushApi'

/**
 * Уведомления в браузере (Web Push, тикет #120).
 *
 * Подписку делает браузер (`PushManager.subscribe` с публичным ключом VAPID сервера), а
 * показывает уведомление service worker `/push-sw.js` — он работает и при закрытой вкладке
 * CRM. Сервер получает адрес подписки и два ключа и шифрует каждое сообщение для этого
 * браузера (RFC 8291).
 */

export const SERVICE_WORKER_URL = '/push-sw.js'

export type BrowserPushState =
  /** Браузер не умеет push (или страница открыта не по https). */
  | 'unsupported'
  /** Человек запретил уведомления сайту — включить их может только он, в настройках браузера. */
  | 'denied'
  | 'off'
  | 'on'

/** Разрешение отклонено: отдельная ошибка, чтобы экран назвал причину, а не «не удалось». */
export class PushPermissionDenied extends Error {
  constructor() {
    super('push.permission_denied')
    this.name = 'PushPermissionDenied'
  }
}

export function browserPushSupported(): boolean {
  return typeof window !== 'undefined'
    && typeof navigator !== 'undefined'
    && 'serviceWorker' in navigator
    && 'PushManager' in window
    && typeof Notification !== 'undefined'
}

async function existingRegistration(): Promise<ServiceWorkerRegistration | undefined> {
  return navigator.serviceWorker.getRegistration(SERVICE_WORKER_URL)
}

export async function browserPushState(): Promise<BrowserPushState> {
  if (!browserPushSupported()) return 'unsupported'
  if (Notification.permission === 'denied') return 'denied'
  const registration = await existingRegistration()
  const subscription = await registration?.pushManager.getSubscription()
  return subscription && Notification.permission === 'granted' ? 'on' : 'off'
}

/**
 * Включает уведомления в этом браузере: разрешение, подписка, регистрация на сервере.
 *
 * Подписка, сделанная на прежний ключ VAPID (администратор создал новые ключи), снимается и
 * делается заново: служба push отвергает подпись другим ключом, и старая подписка молча
 * перестала бы получать что-либо.
 */
export async function enableBrowserPush(publicKey: string): Promise<RegisteredDevice> {
  if (!browserPushSupported()) throw new Error('push.unsupported')
  const permission = await Notification.requestPermission()
  if (permission !== 'granted') throw new PushPermissionDenied()

  const registration = await navigator.serviceWorker.register(SERVICE_WORKER_URL)
  const key = urlBase64ToBytes(publicKey)
  let subscription = await registration.pushManager.getSubscription()
  if (subscription && !sameKey(subscription.options?.applicationServerKey ?? null, key)) {
    await subscription.unsubscribe()
    subscription = null
  }
  if (!subscription) {
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: key,
    })
  }
  return registerDevice(registrationOf(subscription))
}

/** Выключает уведомления в этом браузере: сервер забывает подписку, браузер её снимает. */
export async function disableBrowserPush(): Promise<void> {
  if (!browserPushSupported()) return
  const subscription = await (await existingRegistration())?.pushManager.getSubscription()
  if (!subscription) return
  await unregisterDevice(subscription.endpoint)
  await subscription.unsubscribe()
}

/**
 * Повторяет регистрацию уже подписанного браузера при входе.
 *
 * Браузер время от времени меняет подписку сам, а service worker не может сообщить об этом
 * серверу — у него нет токена. Повтор при каждом открытии CRM обновляет адрес и заодно
 * отметку «последний раз видели». Новую подписку без действия человека не создаёт.
 */
export async function syncBrowserPush(): Promise<void> {
  try {
    if (!browserPushSupported() || Notification.permission !== 'granted') return
    const subscription = await (await existingRegistration())?.pushManager.getSubscription()
    if (subscription) await registerDevice(registrationOf(subscription))
  } catch {
    // Best effort: a failed refresh leaves the previous registration in place.
  }
}

/**
 * Отписка при выходе (путь `signoutRedirect`).
 *
 * Своим `fetch`, а не общим `request`: на 401 тот уводит на вход, а выход не должен
 * превращаться во вход. Ошибки глотаются — выход не задерживается из-за push; подписка в
 * браузере снимается в любом случае, и следующий человек за этим компьютером не получит
 * чужих уведомлений.
 */
export async function forgetBrowserPush(): Promise<void> {
  try {
    if (!browserPushSupported()) return
    const subscription = await (await existingRegistration())?.pushManager.getSubscription()
    if (!subscription) return
    const token = await accessToken()
    try {
      await fetch(`${BASE_URL}/notifications/devices/unregister`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ token: subscription.endpoint }),
      })
    } finally {
      await subscription.unsubscribe()
    }
  } catch {
    // Sign-out goes on regardless.
  }
}

function registrationOf(subscription: PushSubscription) {
  const json = subscription.toJSON()
  return {
    channel: 'webpush' as const,
    token: subscription.endpoint,
    p256dh: json.keys?.p256dh ?? '',
    auth: json.keys?.auth ?? '',
    label: browserLabel(),
  }
}

/** Как человек узнает этот браузер в списке своих устройств. */
export function browserLabel(agent: string = navigator.userAgent): string {
  const browser = /Edg\//.test(agent) ? 'Edge'
    : /YaBrowser\//.test(agent) ? 'Яндекс Браузер'
      : /Firefox\//.test(agent) ? 'Firefox'
        : /Chrome\//.test(agent) ? 'Chrome'
          : /Safari\//.test(agent) ? 'Safari' : 'Браузер'
  const system = /Windows/.test(agent) ? 'Windows'
    : /Android/.test(agent) ? 'Android'
      : /iPhone|iPad/.test(agent) ? 'iOS'
        : /Mac OS X/.test(agent) ? 'macOS'
          : /Linux/.test(agent) ? 'Linux' : ''
  return system ? `${browser}, ${system}` : browser
}

export function urlBase64ToBytes(value: string): Uint8Array<ArrayBuffer> {
  const padded = (value + '='.repeat((4 - (value.length % 4)) % 4)).replace(/-/g, '+').replace(/_/g, '/')
  const raw = atob(padded)
  const bytes = new Uint8Array(new ArrayBuffer(raw.length))
  for (let i = 0; i < raw.length; i += 1) bytes[i] = raw.charCodeAt(i)
  return bytes
}

function sameKey(current: ArrayBuffer | null, wanted: Uint8Array): boolean {
  if (!current) return false
  const bytes = new Uint8Array(current)
  return bytes.length === wanted.length && bytes.every((b, i) => b === wanted[i])
}
